import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import SearchSelect from '../../../components/SearchSelect';
import CreatableCombobox from '../../../components/CreatableCombobox';
import AiBanquetModal from './AiBanquetModal';
import useAuthStore from '../../../store/authStore';
import { confirm } from '../../../components/ConfirmDialog';
import { toAssetUrl } from '../../../utils/assetBase';

// Banket menyu — "miqyaslanan set". Nəfər başına qiymət + hər üzvün öz nisbəti.
// Sifariş/stok mövcud set mexanizmi ilə işləyir (bura yalnız qurma).
// Görünüş: solda banket siyahısı (seçilən sağda açıq) — açıb-bağlama yoxdur.

const labelCls = 'mb-1.5 block text-xs font-semibold text-gray-600';
const fieldCls = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-gray-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';

// Effektiv kateqoriya: QR override varsa ona, yoxsa menyudakı real kateqoriya.
const effectiveCat = (it) => (String(it.category_override || '').trim() || it.category_name);

// Kateqoriyalara qrupla: kateqoriya qrupları ən son əlavə olunan üzvə görə yuxarıda
// (massiv indeksi böyük = yeni). Backend (PDF/QR) də eyni qaydanı işlədir.
const groupByCategory = (items) => {
    const withIdx = items.map((it, idx) => ({ ...it, _idx: idx }));
    const map = new Map();
    for (const it of withIdx) {
        const key = effectiveCat(it);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(it);
    }
    return [...map.entries()]
        .map(([cat, arr]) => ({
            cat,
            items: [...arr].sort((a, b) => a._idx - b._idx),
            maxIdx: Math.max(...arr.map(i => i._idx)),
        }))
        .sort((a, b) => b.maxIdx - a.maxIdx);
};

// Qrupları banketin saxlanmış kateqoriya sırasına görə düz (sırada olmayanlar sona)
const orderGroups = (groups, order = []) =>
    [...groups].sort((a, b) => {
        const ia = order.indexOf(a.cat), ib = order.indexOf(b.cat);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });

const money = (v) => `${Number(v || 0).toFixed(2)} ₼`;
const fmtPrice = (v) => { const n = Number(v || 0); return Number.isInteger(n) ? String(n) : n.toFixed(2); };

export default function Banquet() {
    const navigate = useNavigate();
    const [banquets, setBanquets] = useState([]);
    const [pickable, setPickable] = useState([]);
    const [orders, setOrders] = useState([]);       // banket sifarişləri (ödənilmiş)
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);     // seçilmiş banket
    const pendingSelect = useRef(null);             // yaradılan/kopyalanan banketi yükləndikdən sonra seç
    // Siyahı filtri: «Aktiv» (QR-da görünən) / «Gizli» — gizlilər aktiv siyahıda yer tutmasın
    // Status: active (hamı görür) | draft (yalnız admin, hazırlanır) | hidden (arxiv, heç kim)
    const [listFilter, setListFilter] = useState('active');
    // Sağ panelin tabı: info | maya | items (son seçim yadda qalır)
    const [rightTab, setRightTabState] = useState(() => {
        try { return localStorage.getItem('banquet_right_tab') || 'info'; } catch { return 'info'; }
    });
    const setRightTab = (k) => { setRightTabState(k); try { localStorage.setItem('banquet_right_tab', k); } catch { /* ignore */ } };
    const statusOf = (x) => (Number(x.is_draft || 0) === 1 ? 'draft' : (Number(x.active) === 1 ? 'active' : 'hidden'));
    const inFilter = (x, f = listFilter) => statusOf(x) === f;

    const loadOrders = async () => {
        try { const r = await api.get('/banquet/orders'); setOrders(r.data.data || []); }
        catch { /* ignore */ }
    };
    const setOrderStatus = async (id, status) => {
        try {
            await api.put(`/banquet/orders/${id}`, { status });
            setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
        } catch (err) { toast.error(err.response?.data?.msg || 'Yenilənmədi.'); }
    };
    const [newName, setNewName] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [newMin, setNewMin] = useState('');
    const [creating, setCreating] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);

    // Seçilmiş banketin redaktə vəziyyəti
    const [edit, setEdit] = useState({ name: '', price_per_person: '', min_guests: '', active: true });
    const [items, setItems] = useState([]);
    const [autoSave, setAutoSave] = useState('');  // '' | 'saving' | 'saved'
    const saveTimer = useRef(null);
    const skipSave  = useRef(false);               // load-dan gələn dəyişiklik saxlanmasın

    // Banketin kateqoriya sırası (per-banket)
    const [catOrder, setCatOrder] = useState([]);
    const saveCatOrder = async (cats) => {
        setCatOrder(cats);
        try { await api.put(`/banquet/${openId}/cat-order`, { order: cats }); }
        catch { toast.error('Sıra saxlanmadı.'); }
    };
    const orderedGroups = () => orderGroups(groupByCategory(items), catOrder);
    const moveBanquetCat = (catName, dir) => {
        const cats = orderedGroups().map(g => g.cat);
        const idx = cats.indexOf(catName);
        const swap = idx + dir;
        if (idx < 0 || swap < 0 || swap >= cats.length) return;
        [cats[idx], cats[swap]] = [cats[swap], cats[idx]];
        saveCatOrder(cats);
    };
    // Məhsulu öz kateqoriyası içində ▲▼ (massiv sırası → auto-save). Kateqoriya sırası
    // massiv indeksindən asılı olmasın deyə əvvəlcə cari sıranı «dondururuq».
    const moveItem = (mid, dir) => {
        const idx = items.findIndex(i => Number(i.menu_item_id) === Number(mid));
        if (idx < 0) return;
        const cat = effectiveCat(items[idx]);
        let j = idx + dir;
        while (j >= 0 && j < items.length && effectiveCat(items[j]) !== cat) j += dir;
        if (j < 0 || j >= items.length) return;
        const cats = orderedGroups().map(g => g.cat);
        if (cats.some(c => !catOrder.includes(c))) saveCatOrder(cats);
        const arr = [...items];
        [arr[idx], arr[j]] = [arr[j], arr[idx]];
        setItems(arr);
    };

    // Banket şəkli (QR kartında görünür)
    const [imgUploading, setImgUploading] = useState(false);
    const uploadBanquetImage = async (banquetId, file) => {
        if (!file) return;
        setImgUploading(true);
        try {
            const fd = new FormData();
            fd.append('image', file);
            await api.post(`/menu/sets/${banquetId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
            toast.success('Şəkil yükləndi.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setImgUploading(false);
        }
    };
    const removeBanquetImage = async (banquetId) => {
        if (!(await confirm({ title: 'Şəkli sil', message: 'Banket şəkli silinsin?', tone: 'danger', confirmText: 'Sil' }))) return;
        try {
            await api.delete(`/menu/sets/${banquetId}/image`);
            toast.success('Şəkil silindi.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    // Maya hesabı
    const [maya, setMaya] = useState(null);
    // Son hesablanan qonaq sayı tenant üzrə yadda qalır (hər dəfə 25-ə qayıtmasın)
    const tenantId = useAuthStore((st) => st.user?.tenant_id);
    const mayaKey = `banquet_maya_guests_${tenantId || 'x'}`;
    const [mayaGuests, setMayaGuests] = useState(() => {
        try { return localStorage.getItem(`banquet_maya_guests_${useAuthStore.getState().user?.tenant_id || 'x'}`) || '25'; } catch { return '25'; }
    }); // xam mətn (silib yazmaq olsun), hesablayanda yoxlanır
    const [mayaLoading, setMayaLoading] = useState(false);
    const loadMaya = async (banquetId, guests) => {
        setMayaLoading(true);
        try {
            const res = await api.get(`/banquet/${banquetId}/maya`, { params: { guests } });
            setMaya(res.data);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Maya hesablanmadı.');
        } finally {
            setMayaLoading(false);
        }
    };

    const load = useCallback(async () => {
        try {
            const [b, p] = await Promise.all([api.get('/banquet'), api.get('/banquet/pickable')]);
            setBanquets(b.data.data || []);
            setPickable(p.data.data || []);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); loadOrders(); }, [load]);

    const createBanquet = async () => {
        if (!newName.trim()) { toast.error('Ad yazın.'); return false; }
        setCreating(true);
        try {
            const r = await api.post('/banquet', { name: newName.trim(), price_per_person: newPrice || 0, min_guests: newMin || 0 });
            toast.success('Banket yaradıldı.');
            setNewName(''); setNewPrice(''); setNewMin('');
            pendingSelect.current = r.data?.id || null;
            await load();
            return true;
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yaradılmadı.');
            return false;
        } finally {
            setCreating(false);
        }
    };

    const selectBanquet = (b) => {
        if (!b || openId === b.id) return;
        // Başqa banketə keçəndən əvvəl gözləyən auto-save-i saxla (itməsin)
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
            if (openId) persistItems(openId, items);
        }
        setOpenId(b.id);
        setMaya(null);
        setAddDraft(null);
        setCatOrder(Array.isArray(b.cat_order) ? b.cat_order : []);
        setAutoSave('');
        skipSave.current = true;
        setEdit({ name: b.name, price_per_person: b.price_per_person, min_guests: Number(b.min_guests || 0) || '', status: statusOf(b) });
        setItems((b.items || []).map(i => ({
            menu_item_id: i.menu_item_id,
            qty: Number(i.qty),
            guests_per_bundle: Number(i.guests_per_bundle),
            scale_mode: i.scale_mode, item_name: i.item_name, category_name: i.category_name,
            category_override: i.category_override || '', note: i.note || '', name_override: i.name_override || '',
            qty_unit: i.qty_unit === 'portion' ? 'portion' : 'piece',
            portion_pieces: Math.max(1, Number(i.portion_pieces) || 1),
        })));
    };

    // Siyahı/filtr dəyişəndə: yeni yaradılanı seç (lazımsa filtri ona keçir);
    // seçilmiş filtrdə yoxdursa (və ya silinibsə) filtrin birincisini seç
    useEffect(() => {
        if (pendingSelect.current) {
            const nb = banquets.find(x => x.id === pendingSelect.current);
            if (nb) {
                pendingSelect.current = null;
                if (!inFilter(nb)) { setListFilter(statusOf(nb)); }
                selectBanquet(nb);
                return;
            }
        }
        const visible = banquets.filter((x) => inFilter(x));
        if (!visible.length) { if (openId) { clearTimeout(saveTimer.current); saveTimer.current = null; setOpenId(null); } return; }
        if (!openId || !visible.some(x => x.id === openId)) selectBanquet(visible[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [banquets, listFilter]);

    const saveInfo = async (b) => {
        try {
            await api.put(`/banquet/${b.id}`, {
                name: edit.name, price_per_person: edit.price_per_person, min_guests: edit.min_guests || 0,
                active: edit.status === 'active', is_draft: edit.status === 'draft',
            });
            toast.success('Yadda saxlanıldı.');
            pendingSelect.current = b.id; // aktiv/gizli dəyişibsə filtr ona keçir, banket seçili qalır
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        }
    };

    // Sakit yaddaşa yaz (toast yox, reload yox — redaktəni pozmasın)
    const persistItems = async (banquetId, list) => {
        setAutoSave('saving');
        try {
            await api.put(`/banquet/${banquetId}/items`, {
                items: list.map(i => ({
                    menu_item_id: i.menu_item_id, qty: i.qty,
                    guests_per_bundle: i.guests_per_bundle, scale_mode: i.scale_mode,
                    category_override: i.category_override || '', note: i.note || '', name_override: i.name_override || '',
                    qty_unit: i.qty_unit === 'portion' ? 'portion' : 'piece'
                }))
            });
            setAutoSave('saved');
            setBanquets(prev => prev.map(bq => bq.id === banquetId ? { ...bq, items: list } : bq));
        } catch (err) {
            setAutoSave('');
            toast.error(err.response?.data?.msg || 'Yadda saxlanmadı.');
        }
    };

    // Auto-save: items dəyişəndə 700ms sonra sakit saxla
    useEffect(() => {
        if (skipSave.current) { skipSave.current = false; return; }
        if (!openId) return;
        clearTimeout(saveTimer.current);
        const bid = openId, snapshot = items;
        setAutoSave('saving');
        saveTimer.current = setTimeout(() => { saveTimer.current = null; persistItems(bid, snapshot); }, 700);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    // Səhifədən çıxanda / tab bağlananda gözləyən dəyişikliyi mütləq saxla.
    const flushRef = useRef({ openId: null, items: [] });
    useEffect(() => { flushRef.current = { openId, items }; }, [openId, items]);
    useEffect(() => {
        const flush = () => {
            if (saveTimer.current) {
                clearTimeout(saveTimer.current); saveTimer.current = null;
                const { openId: oid, items: its } = flushRef.current;
                if (oid) persistItems(oid, its);
            }
        };
        window.addEventListener('pagehide', flush);
        window.addEventListener('beforeunload', flush);
        return () => {
            window.removeEventListener('pagehide', flush);
            window.removeEventListener('beforeunload', flush);
            flush();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteBanquet = async (b) => {
        if (!(await confirm({ title: 'Banketi sil', message: `"${b.name}" silinsin?`, tone: 'danger', confirmText: 'Sil' }))) return;
        try {
            await api.delete(`/banquet/${b.id}`);
            toast.success('Silindi.');
            clearTimeout(saveTimer.current); saveTimer.current = null;
            setOpenId(null);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    // Çap / PDF menyusu. Desktop (C#) yeni pəncərə açmır → eyni pəncərədə (orada «← Geri» var).
    const openPrint = (b) => {
        const url = `/admin/banquet/${b.id}/print`;
        if (window.chrome?.webview) { navigate(url); return; }
        const w = window.open(url, '_blank');
        if (!w) navigate(url);
    };

    const duplicateBanquet = async (b) => {
        try {
            const r = await api.post(`/banquet/${b.id}/duplicate`);
            toast.success('Banket kopyalandı.');
            pendingSelect.current = r.data?.id || null;
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Kopyalanmadı.');
        }
    };

    // ── Məhsul əlavə: axtarışdan seç → miqdar pəncərəsi → Enter → yenidən axtarışa ──
    const searchApi = useRef(null);
    const lastScale = useRef(null);   // son əlavə edilən nisbət (növbəti üçün default)
    const [addDraft, setAddDraft] = useState(null); // { pi, qty, gpb, mode }
    const gpbRef = useRef(null);

    const startAdd = (pi) => {
        if (items.some(i => Number(i.menu_item_id) === Number(pi.id))) {
            toast('Bu məhsul artıq banketdədir.');
            setTimeout(() => searchApi.current?.focus(), 0);
            return;
        }
        const last = items[items.length - 1];
        const d = lastScale.current
            || (last ? { qty: last.qty, gpb: last.guests_per_bundle, mode: last.scale_mode } : { qty: 1, gpb: 6, mode: 'scaled' });
        // Vahid: porsiyası 1-dən çox olan məhsulda son seçim (default ədəd); 1 ədədlik məhsulda həmişə ədəd
        const pp = Math.max(1, Number(pi.portion_pieces) || 1);
        setAddDraft({ pi, qty: String(d.qty ?? 1), gpb: String(d.gpb ?? 6), mode: d.mode || 'scaled', unit: pp > 1 ? (d.unit || 'piece') : 'piece' });
    };
    const cancelAdd = () => { setAddDraft(null); setTimeout(() => searchApi.current?.focus(), 0); };
    const confirmAdd = () => {
        if (!addDraft) return;
        const qty = Number(String(addDraft.qty).replace(',', '.'));
        const gpb = Math.max(1, parseInt(addDraft.gpb, 10) || 1);
        if (!(qty > 0)) { toast.error('Miqdarı yazın.'); return; }
        const { pi, mode } = addDraft;
        const pp = Math.max(1, Number(pi.portion_pieces) || 1);
        const unit = pp > 1 && addDraft.unit === 'portion' ? 'portion' : 'piece';
        lastScale.current = { qty, gpb, mode, unit: pp > 1 ? unit : (lastScale.current?.unit || 'piece') };
        setItems(prev => (prev.some(i => Number(i.menu_item_id) === Number(pi.id)) ? prev : [...prev, {
            menu_item_id: pi.id, qty, guests_per_bundle: gpb, scale_mode: mode,
            item_name: pi.name, category_name: pi.category_name, category_override: '', note: '', name_override: '',
            qty_unit: unit, portion_pieces: pp,
        }]));
        setAddDraft(null);
        setTimeout(() => searchApi.current?.focus(), 0);
    };

    const removeItem = (mid) => setItems(items.filter(i => Number(i.menu_item_id) !== Number(mid)));

    // Banket daxilində filtr
    const [itemFilter, setItemFilter] = useState('');

    // ⇄ Əvəz et: sətrin yerində başqa menyu məhsulu (say, nisbət, sıra, QR kateqoriya, qeyd qalır)
    const [replaceFor, setReplaceFor] = useState(null); // menu_item_id
    const replaceApi = useRef(null);
    useEffect(() => { if (replaceFor) setTimeout(() => replaceApi.current?.focus(), 30); }, [replaceFor]);
    const replaceItem = (oldMid, pi) => {
        if (!pi) return;
        if (items.some((i) => Number(i.menu_item_id) === Number(pi.id))) { toast('Bu məhsul artıq banketdədir.'); return; }
        const pp = Math.max(1, Number(pi.portion_pieces) || 1);
        setItems(items.map((i) => (Number(i.menu_item_id) === Number(oldMid) ? {
            ...i,
            menu_item_id: pi.id, item_name: pi.name, category_name: pi.category_name,
            portion_pieces: pp, name_override: '',
            qty_unit: pp > 1 ? i.qty_unit : 'piece', // yeni məhsulun porsiyası 1 ədəddirsə «pors» mənasızdır
        } : i)));
        setReplaceFor(null);
        toast.success(`Əvəz olundu: ${pi.name}`);
    };
    const patchItem = (mid, patch) => setItems(items.map(i => Number(i.menu_item_id) === Number(mid) ? { ...i, ...patch } : i));

    // Məhsulun banketdəki göstəriş adı (QR + PDF). Menyudakı əsl ada qayıdırsa və ya boşdursa → override silinir.
    const [editingItem, setEditingItem] = useState(null);
    const [itemDraft, setItemDraft] = useState('');
    const renameItem = (it, raw) => {
        const v = String(raw || '').trim();
        patchItem(it.menu_item_id, { name_override: (!v || v === it.item_name) ? '' : v });
    };

    // Kateqoriya başlığının adını dəyiş → qrupdakı bütün məhsulların «QR kateqoriya»sı yenilənir
    const [editingCat, setEditingCat] = useState(null);
    const [catDraft, setCatDraft] = useState('');
    const renameCat = (oldName, raw) => {
        const newName = String(raw || '').trim();
        if (!newName || newName === oldName) return;
        setItems(items.map((i) => (effectiveCat(i) === oldName
            ? { ...i, category_override: newName === i.category_name ? '' : newName }
            : i)));
        if (catOrder.includes(oldName)) saveCatOrder(catOrder.map((c) => (c === oldName ? newName : c)));
    };

    const catOptions = [...new Set(pickable.map(p => p.category_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const b = banquets.find(x => x.id === openId) || null;
    const groups = orderedGroups();
    // Banket daxilində filtr (əlavə et axtarışından ayrı): ad, göstəriş adı və ya kateqoriya üzrə
    const fq = itemFilter.trim().toLocaleLowerCase('az');
    const matchItem = (it) => !fq || [it.name_override, it.item_name, effectiveCat(it)]
        .some((v) => String(v || '').toLocaleLowerCase('az').includes(fq));
    const shownGroups = fq
        ? groups.map((g) => ({ ...g, items: g.items.filter(matchItem) })).filter((g) => g.items.length)
        : groups;
    const shownCount = shownGroups.reduce((n, g) => n + g.items.length, 0);

    return (
        <Layout>
            {/* ── Başlıq ── */}
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-black text-gray-800">🍽️ Banket menyu</h1>
                    <p className="mt-0.5 text-sm text-gray-400">
                        Nəfər başına qiymət və hər məhsulun öz nisbəti. Texkartası olan məhsulun stoku avtomatik çıxılır.
                    </p>
                </div>
                {!showCreate && (
                    <div className="flex shrink-0 gap-2">
                    <button onClick={() => setAiOpen(true)}
                        className="rounded-xl border-2 border-primary-200 bg-white px-4 py-2.5 text-sm font-bold text-primary-700 shadow-sm hover:bg-primary-50">
                        ✨ Ağıllı banket yarat
                    </button>
                    <button onClick={() => setShowCreate(true)}
                        className="shrink-0 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-primary-700">
                        ➕ Yeni banket
                    </button>
                    </div>
                )}
            </div>

            {/* ── Banket sifarişləri (beh ödənilmiş) ── */}
            {orders.length > 0 && (
                <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4">
                    <div className="mb-3 flex items-center gap-2">
                        <span className="text-sm font-black text-emerald-800">💳 Banket sifarişləri (beh ödənilib)</span>
                        <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white">{orders.filter(o => o.status === 'yeni').length} yeni</span>
                    </div>
                    <div className="space-y-2">
                        {orders.map((o) => (
                            <div key={o.id} className={`rounded-xl border bg-white p-3 ${o.status === 'yeni' ? 'border-emerald-300' : 'border-gray-200 opacity-70'}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold text-gray-800">{o.banquet_name} · {o.guests} nəfər</div>
                                        <div className="text-[11px] text-gray-500">{o.guest_name} · <span dir="ltr">{String(o.phone).startsWith('994') ? '0' + String(o.phone).slice(3) : o.phone}</span> · {String(o.created_at).slice(0, 16).replace('T', ' ')}</div>
                                        <div className="text-[11px] font-semibold text-emerald-700">Beh: {Number(o.deposit_amount).toFixed(0)} ₼ · Cəmi: {Number(o.total_amount).toFixed(0)} ₼ (qalıq {Number(o.total_amount - o.deposit_amount).toFixed(0)} ₼)</div>
                                    </div>
                                    {o.status === 'yeni' ? (
                                        <div className="flex gap-1.5">
                                            <button onClick={() => setOrderStatus(o.id, 'tesdiq')} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">✓ Təsdiqlə</button>
                                            <button onClick={() => setOrderStatus(o.id, 'red')} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50">Rədd</button>
                                        </div>
                                    ) : (
                                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${o.status === 'tesdiq' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                                            {o.status === 'tesdiq' ? '✓ Təsdiqlənib' : 'Rədd edilib'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Yeni banket ── */}
            {showCreate && (
                <div className="mb-5 rounded-2xl border border-primary-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 text-sm font-black text-gray-800">Yeni banket</div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr_1fr]">
                        <div>
                            <label className={labelCls}>Ad</label>
                            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
                                onKeyDown={async (e) => { if (e.key === 'Enter' && await createBanquet()) setShowCreate(false); }}
                                placeholder="məs. Ad günü banketi" className={fieldCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Qiymət</label>
                            <SuffixInput value={newPrice} onChange={setNewPrice} suffix="₼ / nəfər" step="0.01" placeholder="0" />
                        </div>
                        <div>
                            <label className={labelCls}>Minimum qonaq</label>
                            <SuffixInput value={newMin} onChange={setNewMin} suffix="nəfər" step="1" placeholder="Limit yox" />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                        <button onClick={() => { setShowCreate(false); setNewName(''); setNewPrice(''); setNewMin(''); }}
                            className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-50">Ləğv et</button>
                        <button onClick={async () => { if (await createBanquet()) setShowCreate(false); }} disabled={creating}
                            className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-primary-700 disabled:opacity-50">
                            {creating ? 'Yaradılır...' : '✓ Yarat'}
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="py-16 text-center text-gray-400">Yüklənir...</div>
            ) : banquets.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 text-center">
                    <div className="text-3xl">🍽️</div>
                    <p className="mt-2 text-sm text-gray-400">Hələ banket yoxdur.</p>
                    <button onClick={() => setShowCreate(true)} className="mt-3 rounded-xl bg-primary-600 px-4 py-2 text-sm font-bold text-white hover:bg-primary-700">➕ İlk banketi yarat</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[250px_minmax(0,1fr)]">
                    {/* ── Banket siyahısı (solda; mobildə üfüqi) ── */}
                    <aside className="lg:sticky lg:top-4 lg:self-start">
                        <div className="mb-3 flex rounded-xl bg-gray-100 p-1">
                            {[['active', 'Aktiv'], ['draft', 'Draft'], ['hidden', 'Gizli']].map(([k, label]) => [k, label, banquets.filter((x) => statusOf(x) === k).length]).map(([k, label, n]) => (
                                <button key={k} type="button" onClick={() => setListFilter(k)}
                                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold transition-all ${listFilter === k ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                    {label}
                                    <span className={`rounded-full px-1.5 text-[10px] ${listFilter === k ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-gray-500'}`}>{n}</span>
                                </button>
                            ))}
                        </div>
                        {banquets.filter((x) => inFilter(x)).length === 0 && (
                            <div className="rounded-xl border border-dashed border-gray-200 bg-white px-3 py-6 text-center text-xs text-gray-400">
                                {listFilter === 'active' ? 'Aktiv banket yoxdur.' : listFilter === 'draft' ? 'Draft banket yoxdur.' : 'Gizli banket yoxdur.'}
                            </div>
                        )}
                        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0">
                            {banquets.filter((x) => inFilter(x)).map((x) => {
                                const sel = x.id === openId;
                                return (
                                    <button key={x.id} type="button" onClick={() => selectBanquet(x)}
                                        className={`min-w-[180px] shrink-0 rounded-xl border-2 px-3 py-2.5 text-left transition-all lg:min-w-0 ${sel
                                            ? 'border-primary-500 bg-primary-50 shadow-sm'
                                            : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                        <div className={`truncate text-sm font-bold ${sel ? 'text-primary-800' : 'text-gray-800'}`}>{x.name}</div>
                                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                                            <span className="font-black text-primary-700">{fmtPrice(x.price_per_person)} ₼/nəfər</span>
                                            {Number(x.min_guests || 0) > 0 && <span className="font-semibold text-amber-700">min {Number(x.min_guests)}</span>}
                                            <span className="text-gray-400">{x.items?.length || 0} məhsul</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </aside>

                    {/* ── Seçilmiş banket ── */}
                    {b && (
                        <div className="min-w-0 space-y-4">
                            {/* Başlıq + əməliyyatlar */}
                            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4">
                                {b.image_url ? (
                                    <img src={toAssetUrl(b.image_url)} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
                                ) : (
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-xl">🍽️</div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-lg font-black text-gray-800">{b.name}</div>
                                    <div className="text-xs text-gray-400">
                                        {fmtPrice(b.price_per_person)} ₼ / nəfər
                                        {Number(b.min_guests || 0) > 0 && ` · minimum ${Number(b.min_guests)} nəfər`}
                                        {` · ${items.length} məhsul`}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button onClick={() => openPrint(b)} className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 hover:bg-gray-50 hover:text-primary-700">🖨️ PDF / Çap</button>
                                    <button onClick={() => duplicateBanquet(b)} className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 hover:bg-gray-50 hover:text-primary-700">📋 Kopyala</button>
                                    <button onClick={() => deleteBanquet(b)} className="rounded-xl border border-red-100 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-50">🗑️ Sil</button>
                                </div>
                            </div>

                            {/* ── Bölmələr: 3 tab (hamısı bir səhifədə sürüşmür) ── */}
                            <div className="flex gap-1 rounded-2xl border border-gray-200 bg-white p-1">
                                {[['info', 'Əsas məlumat'], ['maya', '🧮 Maya hesabı'], ['items', `Məhsullar (${items.length})`]].map(([k, label]) => (
                                    <button key={k} type="button" onClick={() => setRightTab(k)}
                                        className={`flex-1 rounded-xl py-2.5 text-sm font-bold transition-all ${rightTab === k ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {/* ── Məhsullar ── */}
                            {rightTab === 'items' && (<>
                            <Section
                                title={`Məhsullar (${items.length})`}
                                right={autoSave === 'saving'
                                    ? <span className="text-[11px] font-semibold text-gray-400">saxlanılır...</span>
                                    : autoSave === 'saved' ? <span className="text-[11px] font-semibold text-emerald-600">✓ avtomatik saxlanıldı</span> : null}
                            >
                                <div className="mb-1.5 text-xs font-semibold text-gray-600">➕ Məhsul əlavə et</div>
                                <SearchSelect
                                    value=""
                                    apiRef={searchApi}
                                    placeholder="Menyudan məhsul axtar, Enter ilə seç..."
                                    options={pickable.map((pi) => ({
                                        value: pi.id,
                                        label: `${pi.name} · ${pi.category_name}`,
                                        disabled: items.some(i => Number(i.menu_item_id) === Number(pi.id))
                                    }))}
                                    onChange={(id) => {
                                        const pi = pickable.find(p => String(p.id) === String(id));
                                        if (pi) startAdd(pi);
                                    }}
                                />

                                {items.length === 0 ? (
                                    <div className="mt-4 rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">
                                        Hələ məhsul yoxdur. Yuxarıdan axtarıb əlavə et.
                                    </div>
                                ) : (
                                    <>
                                    {/* Banketdəki məhsullarda filtr — əlavə et axtarışından ayrı */}
                                    <div className="mt-5 flex items-center gap-2 border-t border-gray-100 pt-4">
                                        <div className="flex flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 focus-within:border-primary-400 focus-within:bg-white">
                                            <span className="text-sm">🔎</span>
                                            <input value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Escape') setItemFilter(''); }}
                                                placeholder="Banketdəki məhsullarda süz (məs. mimoza)"
                                                className="w-full bg-transparent py-2 text-sm text-gray-800 focus:outline-none" />
                                            {itemFilter && (
                                                <button type="button" onClick={() => setItemFilter('')} className="text-gray-400 hover:text-gray-600" title="Təmizlə">✕</button>
                                            )}
                                        </div>
                                        {fq && <span className="shrink-0 text-xs font-semibold text-gray-500">{shownCount} / {items.length}</span>}
                                    </div>
                                    {fq && shownCount === 0 && (
                                        <div className="mt-3 rounded-xl border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">«{itemFilter}» banketdə tapılmadı.</div>
                                    )}
                                    <div className="mt-4 space-y-4">
                                        {shownGroups.map((g, gi) => (
                                            <div key={g.cat}>
                                                {/* Kateqoriya başlığı: ad (✏️) + ▲▼ */}
                                                <div className="mb-1.5 flex items-center gap-2 px-1">
                                                    {editingCat === g.cat ? (
                                                        <input autoFocus value={catDraft}
                                                            onChange={(e) => setCatDraft(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') { renameCat(g.cat, catDraft); setEditingCat(null); }
                                                                else if (e.key === 'Escape') setEditingCat(null);
                                                            }}
                                                            onBlur={() => { renameCat(g.cat, catDraft); setEditingCat(null); }}
                                                            className="w-56 rounded-lg border border-primary-300 px-2 py-1 text-xs font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-100" />
                                                    ) : (
                                                        <button type="button" onClick={() => { setEditingCat(g.cat); setCatDraft(g.cat); }}
                                                            title="Kateqoriyanın adını dəyiş (QR və PDF-də belə görünür)"
                                                            className="group flex items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-primary-50">
                                                            <span className="text-xs font-black uppercase tracking-wider text-primary-700">{g.cat}</span>
                                                            <span className="text-[11px] text-gray-300 group-hover:text-primary-600">✏️</span>
                                                        </button>
                                                    )}
                                                    <span className="text-[11px] text-gray-400">{g.items.length}</span>
                                                    {!fq && (
                                                        <div className="ml-auto flex gap-1">
                                                            <ArrowBtn dir="up" disabled={gi === 0} onClick={() => moveBanquetCat(g.cat, -1)} title="Kateqoriyanı yuxarı" />
                                                            <ArrowBtn dir="down" disabled={gi === groups.length - 1} onClick={() => moveBanquetCat(g.cat, 1)} title="Kateqoriyanı aşağı" />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
                                                    {g.items.map((it, ii) => {
                                                        const fixed = it.scale_mode === 'fixed';
                                                        return (
                                                            <div key={it.menu_item_id} className="p-3">
                                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                                                    <div className={`flex flex-col ${fq ? 'invisible' : ''}`}>
                                                                        <button type="button" disabled={ii === 0} onClick={() => moveItem(it.menu_item_id, -1)} title="Yuxarı"
                                                                            className="px-1 text-[11px] leading-none text-gray-400 hover:text-primary-600 disabled:opacity-20">▲</button>
                                                                        <button type="button" disabled={ii === g.items.length - 1} onClick={() => moveItem(it.menu_item_id, 1)} title="Aşağı"
                                                                            className="px-1 text-[11px] leading-none text-gray-400 hover:text-primary-600 disabled:opacity-20">▼</button>
                                                                    </div>
                                                                    <div className="min-w-0 flex-1 basis-40">
                                                                        {editingItem === it.menu_item_id ? (
                                                                            <input autoFocus value={itemDraft}
                                                                                onChange={(e) => setItemDraft(e.target.value)}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') { renameItem(it, itemDraft); setEditingItem(null); }
                                                                                    else if (e.key === 'Escape') setEditingItem(null);
                                                                                }}
                                                                                onBlur={() => { renameItem(it, itemDraft); setEditingItem(null); }}
                                                                                placeholder={it.item_name}
                                                                                className="w-full max-w-xs rounded-lg border border-primary-300 px-2 py-1 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-100" />
                                                                        ) : (
                                                                            <button type="button" onClick={() => { setEditingItem(it.menu_item_id); setItemDraft(it.name_override || it.item_name); }}
                                                                                title="Banketdə görünən adı dəyiş (QR və PDF). Menyudakı ad dəyişmir."
                                                                                className="group flex max-w-full items-center gap-1.5 rounded-md text-left">
                                                                                <span className="truncate text-sm font-semibold text-gray-800">{it.name_override || it.item_name}</span>
                                                                                <span className="shrink-0 text-[11px] text-gray-300 group-hover:text-primary-600">✏️</span>
                                                                            </button>
                                                                        )}
                                                                        {it.name_override && editingItem !== it.menu_item_id && (
                                                                            <div className="truncate text-[11px] text-gray-400">menyuda: {it.item_name}</div>
                                                                        )}
                                                                    </div>

                                                                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                                        <input type="number" min="0" step="0.001" value={it.qty}
                                                                            onChange={(e) => patchItem(it.menu_item_id, { qty: e.target.value })}
                                                                            className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-center text-sm font-bold text-gray-800 focus:border-primary-400 focus:outline-none" />
                                                                        {Number(it.portion_pieces) > 1 ? (
                                                                            <UnitToggle unit={it.qty_unit} onChange={(u) => patchItem(it.menu_item_id, { qty_unit: u })} />
                                                                        ) : <span>ədəd</span>}
                                                                        <ModeToggle fixed={fixed} onChange={(m) => patchItem(it.menu_item_id, { scale_mode: m })} />
                                                                        {!fixed && (
                                                                            <>
                                                                                <input type="number" min="1" value={it.guests_per_bundle}
                                                                                    onChange={(e) => patchItem(it.menu_item_id, { guests_per_bundle: e.target.value })}
                                                                                    className="w-14 rounded-lg border border-gray-200 px-2 py-1.5 text-center text-sm font-bold text-gray-800 focus:border-primary-400 focus:outline-none" />
                                                                                <span>nəfərə</span>
                                                                            </>
                                                                        )}
                                                                        <UnitHint qty={it.qty} unit={it.qty_unit} pp={it.portion_pieces} />
                                                                    </div>
                                                                    <button onClick={() => setReplaceFor(it.menu_item_id)} title="Başqa məhsulla əvəz et (say və nisbət qalır)"
                                                                        className="flex h-8 items-center gap-1 rounded-lg border border-gray-200 px-2 text-xs font-bold text-gray-500 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700">⇄ Əvəz et</button>
                                                                    <button onClick={() => removeItem(it.menu_item_id)} title="Banketdən çıxar"
                                                                        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-300 hover:bg-red-50 hover:text-red-500">✕</button>
                                                                </div>

                                                                <div className="mt-2 grid grid-cols-1 gap-2 pl-6 sm:grid-cols-2">
                                                                    <div title="Banket QR və PDF-də bu məhsul hansı kateqoriyada görünsün. Boş = menyudakı kateqoriya.">
                                                                        <CreatableCombobox
                                                                            value={it.category_override || ''}
                                                                            onChange={(v) => patchItem(it.menu_item_id, { category_override: v })}
                                                                            options={catOptions}
                                                                            placeholder={`QR kateqoriya: ${it.category_name}`}
                                                                            inputClassName="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-600 focus:border-primary-400 focus:bg-white focus:outline-none"
                                                                        />
                                                                    </div>
                                                                    <input type="text" value={it.note || ''}
                                                                        onChange={(e) => patchItem(it.menu_item_id, { note: e.target.value })}
                                                                        placeholder="QR qeyd (məs. Qazlı, Qazsız)"
                                                                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-600 focus:border-primary-400 focus:bg-white focus:outline-none" />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                )}
                            </Section>

                            </>)}

                            {/* ── Əsas məlumat ── */}
                            {rightTab === 'info' && (<>
                            <Section title="Əsas məlumat">
                                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_240px]">
                                    <div className="space-y-4">
                                        <div>
                                            <label className={labelCls}>Ad</label>
                                            <input type="text" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={fieldCls} />
                                        </div>
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <div>
                                                <label className={labelCls}>Qiymət</label>
                                                <SuffixInput value={edit.price_per_person} onChange={(v) => setEdit({ ...edit, price_per_person: v })} suffix="₼ / nəfər" step="0.01" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Minimum qonaq</label>
                                                <SuffixInput value={edit.min_guests} onChange={(v) => setEdit({ ...edit, min_guests: v })} suffix="nəfər" step="1" placeholder="Limit yox" />
                                            </div>
                                        </div>
                                        {Number(edit.min_guests) > 0 && (
                                            <p className="-mt-2 text-[11px] text-gray-400">Kassir və ofisiant {Number(edit.min_guests)} nəfərdən az yaza bilməz. Admin yaza bilər.</p>
                                        )}
                                        <div>
                                            <label className={labelCls}>Status</label>
                                            <div className="grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1">
                                                {[['active', 'Aktiv'], ['draft', 'Draft'], ['hidden', 'Gizli']].map(([k, label]) => (
                                                    <button key={k} type="button" onClick={() => setEdit({ ...edit, status: k })}
                                                        className={`rounded-lg py-2 text-sm font-bold ${edit.status === k ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="mt-1.5 text-[11px] text-gray-400">
                                                {edit.status === 'active' && 'QR menyuda, kassirdə və ofisiantda görünür.'}
                                                {edit.status === 'draft' && 'Hazırlanır: yalnız admin görür. QR, kassir və ofisiant görmür.'}
                                                {edit.status === 'hidden' && 'Arxiv: heç yerdə görünmür.'}
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        <label className={labelCls}>QR kart şəkli</label>
                                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                                            {b.image_url ? (
                                                <img src={toAssetUrl(b.image_url)} alt={b.name} className="aspect-[4/3] w-full object-cover" />
                                            ) : (
                                                <div className="flex aspect-[4/3] w-full flex-col items-center justify-center bg-gray-50 text-gray-300">
                                                    <span className="text-3xl">🖼️</span>
                                                    <span className="mt-1 text-[11px]">Şəkil yoxdur</span>
                                                </div>
                                            )}
                                            <div className="flex gap-2 p-2">
                                                <label className="flex-1 cursor-pointer rounded-lg bg-primary-50 py-2 text-center text-xs font-bold text-primary-700 hover:bg-primary-100">
                                                    {imgUploading ? 'Yüklənir...' : (b.image_url ? '🔄 Dəyiş' : '⬆️ Yüklə')}
                                                    <input type="file" accept="image/*" className="hidden" disabled={imgUploading}
                                                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadBanquetImage(b.id, f); }} />
                                                </label>
                                                {b.image_url && (
                                                    <button onClick={() => removeBanquetImage(b.id)}
                                                        className="rounded-lg border border-red-200 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-50">Sil</button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 flex justify-end border-t border-gray-100 pt-4">
                                    <button onClick={() => saveInfo(b)} className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-primary-700">
                                        💾 Yadda saxla
                                    </button>
                                </div>
                            </Section>

                            </>)}

                            {/* ── Maya hesabı ── */}
                            {rightTab === 'maya' && (<>
                            <Section title="🧮 Maya hesabı">
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="w-40">
                                        <SuffixInput value={mayaGuests} onChange={(v) => setMayaGuests(String(v).replace(/[^\d]/g, ''))} suffix="nəfər" step="1" />
                                    </div>
                                    <button
                                        onClick={async () => {
                                            const g = parseInt(mayaGuests, 10);
                                            if (!(g > 0)) { toast.error('Qonaq sayını yazın.'); return; }
                                            try { localStorage.setItem(mayaKey, String(g)); } catch { /* ignore */ }
                                            await persistItems(b.id, items); loadMaya(b.id, g);
                                        }}
                                        disabled={mayaLoading}
                                        className="rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50">
                                        {mayaLoading ? 'Hesablanır...' : 'Hesabla'}
                                    </button>
                                </div>

                                {maya && (
                                    <div className="mt-4">
                                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                                            <table className="w-full min-w-[420px] text-sm">
                                                <thead>
                                                    <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500">
                                                        <th className="px-3 py-2"></th>
                                                        <th className="px-3 py-2 text-right">1 nəfər</th>
                                                        <th className="px-3 py-2 text-right">{maya.guests} nəfər</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    <tr>
                                                        <td className="px-3 py-2 text-gray-600">Menyu satış qiyməti ilə</td>
                                                        <td className="px-3 py-2 text-right font-semibold text-gray-800">{money(maya.per_person_sale)}</td>
                                                        <td className="px-3 py-2 text-right font-semibold text-gray-800">{money(maya.total_sale)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="px-3 py-2 text-gray-600">Banket qiyməti</td>
                                                        <td className="px-3 py-2 text-right font-black text-primary-700">{money(maya.price_per_person)}</td>
                                                        <td className="px-3 py-2 text-right font-black text-primary-700">{money(maya.banquet_total)}</td>
                                                    </tr>
                                                    <tr className="bg-rose-50/40">
                                                        <td className="px-3 py-2 text-gray-600">
                                                            {Number(maya.discount_total) >= 0 ? 'Endirim' : 'Menyudan baha'}
                                                            {Number(maya.total_sale) > 0 && <span className="ml-1 text-[11px] text-gray-400">({Math.abs(Number(maya.discount_percent))}%)</span>}
                                                        </td>
                                                        <td className={`px-3 py-2 text-right font-bold ${Number(maya.discount_total) >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{money(Math.abs(maya.discount_per_person))}</td>
                                                        <td className={`px-3 py-2 text-right font-bold ${Number(maya.discount_total) >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{money(Math.abs(maya.discount_total))}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="px-3 py-2 text-gray-600">Maya (alış)</td>
                                                        <td className="px-3 py-2 text-right font-semibold text-amber-700">{money(maya.per_person_cost)}</td>
                                                        <td className="px-3 py-2 text-right font-semibold text-amber-700">{money(maya.total_cost)}</td>
                                                    </tr>
                                                    <tr className="bg-emerald-50/40">
                                                        <td className="px-3 py-2 font-semibold text-gray-700">Mənfəət (banket − maya)</td>
                                                        <td className={`px-3 py-2 text-right font-black ${Number(maya.per_person_margin) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{money(maya.per_person_margin)}</td>
                                                        <td className={`px-3 py-2 text-right font-black ${Number(maya.total_margin) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{money(maya.total_margin)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {maya.missing_recipe?.length > 0 && (
                                            <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-600">
                                                ⚠️ Texkartası (və ya maya dəyəri) olmayan məhsullar mayaya <b>daxil edilmədi</b>: {maya.missing_recipe.join(', ')}. Bunlara texkarta yazsan maya dəqiq olacaq.
                                            </div>
                                        )}

                                        <details className="mt-3" open>
                                            <summary className="cursor-pointer text-xs font-semibold text-amber-700">Məhsul üzrə açılış ({maya.guests} nəfər)</summary>
                                            <div className="mt-1 overflow-x-auto">
                                                <table className="w-full min-w-[620px] text-xs">
                                                    <thead><tr className="text-left text-gray-400">
                                                        <th className="py-1.5 pr-2">Məhsul</th><th className="px-1">Say</th>
                                                        <th className="px-1 text-right">Satış (1 vahid)</th><th className="px-1 text-right">Satış cəmi</th>
                                                        <th className="px-1 text-right">Maya (1 vahid)</th><th className="pl-1 text-right">Maya cəmi</th>
                                                    </tr></thead>
                                                    <tbody>
                                                        {maya.items.map((it, i) => (
                                                            <tr key={i} className="border-t border-gray-100">
                                                                <td className="py-1 pr-2 text-gray-700">
                                                                    {it.name}
                                                                    {Number(it.portion_pieces) > 1 && <span className="ml-1 text-[10px] text-amber-600">(1 pors={it.portion_pieces} əd)</span>}
                                                                    {!it.has_recipe && <span className="ml-1 text-red-500">⚠️</span>}
                                                                </td>
                                                                <td className="px-1 whitespace-nowrap">
                                                                    {it.display_count ?? it.count} {it.unit_label || 'əd'}
                                                                    {it.unit_label === 'pors' && <span className="ml-1 text-[10px] text-gray-400">({it.count} əd)</span>}
                                                                </td>
                                                                <td className="px-1 text-right text-gray-600">{money(it.display_unit_sale ?? it.unit_sale)}</td>
                                                                <td className="px-1 text-right font-semibold text-gray-800">{money(it.line_sale)}</td>
                                                                <td className="px-1 text-right text-gray-600">{money(it.display_unit_cost ?? it.unit_cost)}</td>
                                                                <td className="pl-1 text-right font-semibold text-amber-700">{money(it.line_cost)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="border-t-2 border-gray-200 font-black">
                                                            <td className="py-1.5 pr-2 text-gray-700" colSpan={3}>Cəmi</td>
                                                            <td className="px-1 text-right text-gray-800">{money(maya.total_sale)}</td>
                                                            <td className="px-1"></td>
                                                            <td className="pl-1 text-right text-amber-700">{money(maya.total_cost)}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </details>
                                    </div>
                                )}
                            </Section>
                            </>)}
                        </div>
                    )}
                </div>
            )}

            <AiBanquetModal open={aiOpen} onClose={() => setAiOpen(false)}
                onCreated={(id) => { if (id) pendingSelect.current = id; load(); }} />

            {/* ── ⇄ Əvəz et pəncərəsi ── */}
            {replaceFor && (() => {
                const cur = items.find((i) => Number(i.menu_item_id) === Number(replaceFor));
                if (!cur) return null;
                return (
                    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[12vh]" onMouseDown={() => setReplaceFor(null)}>
                        <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => { if (e.key === 'Escape') setReplaceFor(null); }}>
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Əvəz et</div>
                            <div className="mb-1 text-lg font-black text-gray-800">{cur.name_override || cur.item_name}</div>
                            <div className="mb-4 text-xs text-gray-500">
                                Say və nisbət qalır: {cur.qty} {cur.qty_unit === 'portion' ? 'pors' : 'ədəd'} · {cur.scale_mode === 'fixed' ? 'sabit' : `hər ${cur.guests_per_bundle} nəfərə`}
                            </div>
                            <SearchSelect
                                value=""
                                apiRef={replaceApi}
                                placeholder="Yeni məhsulu axtar, Enter ilə seç..."
                                options={pickable.map((pi) => ({
                                    value: pi.id,
                                    label: `${pi.name} · ${pi.category_name}`,
                                    disabled: items.some((i) => Number(i.menu_item_id) === Number(pi.id)),
                                }))}
                                onChange={(id) => replaceItem(replaceFor, pickable.find((p) => String(p.id) === String(id)))}
                            />
                            <div className="mt-4 flex justify-end">
                                <button type="button" onClick={() => setReplaceFor(null)}
                                    className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-50">Ləğv et</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Miqdar pəncərəsi (axtarışdan seçiləndə) ── */}
            {addDraft && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={cancelAdd}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancelAdd(); } }}>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{addDraft.pi.category_name}</div>
                        <div className="mb-4 text-lg font-black text-gray-800">{addDraft.pi.name}</div>

                        <label className={labelCls}>Miqdar</label>
                        <div className="flex items-center gap-2">
                            <input autoFocus type="number" min="0" step="0.001" value={addDraft.qty}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => setAddDraft({ ...addDraft, qty: e.target.value })}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter') return;
                                    e.preventDefault();
                                    if (addDraft.mode === 'scaled' && gpbRef.current) gpbRef.current.focus();
                                    else confirmAdd();
                                }}
                                className="w-24 rounded-xl border-2 border-primary-300 px-3 py-2.5 text-center text-xl font-black text-gray-800 focus:border-primary-500 focus:outline-none" />
                            {Number(addDraft.pi.portion_pieces) > 1 ? (
                                <UnitToggle unit={addDraft.unit} onChange={(u) => setAddDraft({ ...addDraft, unit: u })} />
                            ) : <span className="text-sm text-gray-500">ədəd</span>}
                            <ModeToggle fixed={addDraft.mode === 'fixed'} onChange={(m) => setAddDraft({ ...addDraft, mode: m })} />
                        </div>
                        {Number(addDraft.pi.portion_pieces) > 1 && (
                            <div className="mt-1.5 text-[11px] text-amber-700">
                                1 porsiya = {addDraft.pi.portion_pieces} ədəd <UnitHint qty={addDraft.qty} unit={addDraft.unit} pp={addDraft.pi.portion_pieces} />
                            </div>
                        )}

                        {addDraft.mode === 'scaled' && (
                            <div className="mt-3 flex items-center gap-2">
                                <span className="text-sm text-gray-500">hər</span>
                                <input ref={gpbRef} type="number" min="1" value={addDraft.gpb}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => setAddDraft({ ...addDraft, gpb: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmAdd(); } }}
                                    className="w-20 rounded-xl border-2 border-gray-200 px-3 py-2 text-center text-lg font-black text-gray-800 focus:border-primary-500 focus:outline-none" />
                                <span className="text-sm text-gray-500">nəfərə</span>
                            </div>
                        )}

                        <p className="mt-3 text-xs text-gray-400">
                            {addDraft.mode === 'fixed'
                                ? `Qonaq sayından asılı olmayaraq ${addDraft.qty || 0} ${addDraft.unit === 'portion' ? 'porsiya' : 'ədəd'}.`
                                : `Hər ${addDraft.gpb || 1} qonağa ${addDraft.qty || 0} ${addDraft.unit === 'portion' ? 'porsiya' : 'ədəd'}.`}
                            {' '}Enter ilə növbəti xanaya keç, sonra əlavə et.
                        </p>

                        <div className="mt-5 flex gap-2">
                            <button onClick={cancelAdd} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-50">Ləğv et</button>
                            <button onClick={confirmAdd} className="flex-1 rounded-xl bg-primary-600 py-2.5 text-sm font-bold text-white hover:bg-primary-700">✓ Əlavə et</button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}

// ── Kiçik UI hissələri ──
function Section({ title, right, children }) {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
                <h3 className="text-sm font-black text-gray-800">{title}</h3>
                {right}
            </div>
            {children}
        </div>
    );
}

// Rəqəm xanası + sağda vahid (₼ / nəfər, nəfər)
function SuffixInput({ value, onChange, suffix, step = '1', placeholder = '' }) {
    return (
        <div className="flex items-center overflow-hidden rounded-xl border border-gray-200 bg-white focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <input type="number" min="0" step={step} value={value} placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full min-w-0 bg-transparent px-3 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none" />
            <span className="shrink-0 border-l border-gray-100 bg-gray-50 px-3 py-2.5 text-xs font-semibold text-gray-500">{suffix}</span>
        </div>
    );
}

// Miqdar vahidi: ədəd | porsiya (yalnız 1 porsiyası bir neçə ədəd olan məhsullarda)
function UnitToggle({ unit, onChange }) {
    const pors = unit === 'portion';
    return (
        <div className="flex rounded-lg bg-amber-50 p-0.5 ring-1 ring-amber-100">
            <button type="button" onClick={() => onChange('piece')}
                className={`rounded-md px-2.5 py-1 text-[11px] font-bold ${!pors ? 'bg-white text-amber-700 shadow-sm' : 'text-amber-600/70'}`}>ədəd</button>
            <button type="button" onClick={() => onChange('portion')}
                className={`rounded-md px-2.5 py-1 text-[11px] font-bold ${pors ? 'bg-white text-amber-700 shadow-sm' : 'text-amber-600/70'}`}>pors</button>
        </div>
    );
}

// İpucu: ədəd yazılıbsa «= 1,5 pors», porsiya yazılıbsa «= 6 əd»
function UnitHint({ qty, unit, pp }) {
    const n = Number(String(qty ?? '').replace(',', '.'));
    const p = Math.max(1, Number(pp) || 1);
    if (p <= 1 || !(n > 0)) return null;
    const v = unit === 'portion' ? n * p : n / p;
    const txt = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace('.', ',');
    return <span className="whitespace-nowrap text-[11px] font-semibold text-amber-600">= {txt} {unit === 'portion' ? 'əd' : 'pors'}</span>;
}

// «hər N nəfərə» / «sabit» seçimi
function ModeToggle({ fixed, onChange }) {
    return (
        <div className="flex rounded-lg bg-gray-100 p-0.5">
            <button type="button" onClick={() => onChange('scaled')}
                className={`rounded-md px-2.5 py-1 text-[11px] font-bold ${!fixed ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'}`}>hər</button>
            <button type="button" onClick={() => onChange('fixed')}
                className={`rounded-md px-2.5 py-1 text-[11px] font-bold ${fixed ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'}`}>sabit</button>
        </div>
    );
}

// Kateqoriya sırası üçün aydın ▲▼ düyməsi
function ArrowBtn({ dir, disabled, onClick, title }) {
    return (
        <button type="button" disabled={disabled} onClick={onClick} title={title}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs text-gray-500 hover:border-primary-300 hover:text-primary-600 disabled:opacity-25">
            {dir === 'up' ? '▲' : '▼'}
        </button>
    );
}
